import { tr } from '@/lib/i18n-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PEOPLE_QUERY_KEY, PERSON_QUERY_KEY } from './types';

function useRefreshPeople() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: PERSON_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };
}

export function WebSignInSwitch({ user }: { user: { id: string; name: string; cloudLoginAllowed: boolean } }) {
  const toast = useToast();
  const refresh = useRefreshPeople();

  const change = useMutation({
    mutationFn: () => put(`/org/users/${user.id}/cloud-login`, { allowed: !user.cloudLoginAllowed }),
    onSuccess: () => {
      toast.success(user.cloudLoginAllowed ? tr('admin', 'x_can_no_longer_sign_in_on_the_web', { name: user.name }) : tr('admin', 'x_can_sign_in_on_the_web', { name: user.name }));
      refresh();
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_change_web_sign_in'), describeError(caught)),
  });

  return (
    <Button
      type="button"
      variant={user.cloudLoginAllowed ? 'secondary' : 'outline'}
      size="sm"
      loading={change.isPending}
      onClick={(event) => {
        event.stopPropagation();
        change.mutate();
      }}
    >
      {user.cloudLoginAllowed ? tr('admin', 'allowed_2') : tr('admin', 'not_allowed_2')}
    </Button>
  );
}

export function UnlockButton({ user }: { user: { id: string; name: string } }) {
  const toast = useToast();
  const refresh = useRefreshPeople();

  const unlock = useMutation({
    mutationFn: () => post(`/org/users/${user.id}/unlock`),
    onSuccess: () => {
      toast.success(tr('admin', 'x_can_sign_in_again', { name: user.name }));
      refresh();
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_unlock_the_account'), describeError(caught)),
  });

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      loading={unlock.isPending}
      onClick={(event) => {
        event.stopPropagation();
        unlock.mutate();
      }}
    >
      {tr('admin', 'unlock')}
    </Button>
  );
}
